import React, { useEffect, useState, useRef } from 'react'
import { useDataValue, useDataSubmit, useDataChange } from 'Simple/Data.js'
import { TASK_PRIORITIES_ORDER } from 'Data/constants.js'
import { ControlledBoard, moveCard } from '@local/react-kanban'
import View from './view.js'
import Card from './Card'
import EmptyCard from './EmptyCard'
import StageHeader from './StageHeader'
import '@local/react-kanban/dist/styles.css'
import './style.css'

function sortCards(cards, sortSelected) {
  if (!sortSelected || Object.values(sortSelected).every(v => v == null)) {
    return [...cards].sort((a, b) => a.order - b.order)
  }

  let sortField = Object.keys(sortSelected).find(
    key => sortSelected[key] !== null
  )
  let sortDirection = sortSelected[sortField]

  let sorted = [...cards].sort((a, b) => {
    let aValue = a[sortField]
    let bValue = b[sortField]

    if (aValue == null && bValue == null) return 0
    if (aValue == null) return 1
    if (bValue == null) return -1

    if (sortField === 'priority') {
      let priorityOrder = Object.fromEntries(
        Object.values(TASK_PRIORITIES_ORDER).map(({ label, order }) => [
          label,
          order,
        ])
      )
      aValue = priorityOrder[aValue] || 0
      bValue = priorityOrder[bValue] || 0
    } else if (sortField === 'due_date' || sortField === 'created_at') {
      aValue = new Date(aValue).getTime()
      bValue = new Date(bValue).getTime()
    }

    if (aValue === bValue) return a.order - b.order
    return aValue < bValue ? -1 : 1
  })

  return sortDirection === 'desc' ? sorted.reverse() : sorted
}

export default function Logic(props) {
  let [stageSorts, setStageSorts] = useState({})
  let skipSortRef = useRef(false)
  let clearSortFnsRef = useRef({})

  let stages = useDataValue({
    context: 'board',
    path: 'stages',
    viewPath: props.viewPath,
  })

  let tasks = useDataValue({
    context: 'board_tasks',
    path: 'tasks_tasks',
    viewPath: props.viewPath,
  })

  let submit = useDataSubmit({
    context: 'tab',
    viewPath: props.viewPath,
  })

  let changeFilters = useDataChange({
    context: 'filters',
    viewPath: props.viewPath,
  })

  let [board, setBoard] = useState({
    columns: [],
  })

  let handleSortChange = (stageId, sortSelected) => {
    setStageSorts(current => ({
      ...current,
      [stageId]: sortSelected,
    }))
  }

  let handleRegisterClearSort = (stageId, clearFn) => {
    clearSortFnsRef.current[stageId] = clearFn
  }

  useEffect(() => {
    // Skip rebuilding during manual card moves to preserve optimistic update
    if (skipSortRef.current) {
      skipSortRef.current = false
      return
    }

    setBoard({
      columns: (stages || []).map(({ id, text: title, subscribers }) => {
        let unsortedCards = (tasks || [])
          .map(({ patients, assignees, ...task }) => ({
            ...task,
            patient: patients[0],
            assignee: assignees[0],
          }))
          .filter(task => task.stage_id === id)

        return {
          id,
          title,
          is_current_user_subscribed: subscribers?.length > 0,
          cards: sortCards(unsortedCards, stageSorts[id]),
        }
      }),
    })
  }, [stages, tasks, stageSorts])

  let handleCardMove = (card, source, destination) => {
    if (card?.placeholder) return
    let destinationColumn = board.columns.find(
      column => column.id === destination.toColumnId
    )
    if (!destinationColumn) {
      console.error('Destination column not found:', destination.toColumnId)
      return
    }

    // Check if destination column has active sort
    let destinationSort = stageSorts[destination.toColumnId]
    let isSorted =
      destinationSort && Object.values(destinationSort).some(v => v !== null)

    if (isSorted) {
      // handle update all cards
      let updatedCards = [...destinationColumn.cards]

      if (source.fromColumnId === destination.toColumnId) {
        updatedCards.splice(source.fromPosition, 1)
      }

      updatedCards.splice(destination.toPosition, 0, card)

      // calculate new orders for cards
      let startOrder = Date.now()
      let orderGap = 1000

      let updates = updatedCards
        .filter(c => !c.placeholder)
        .map((c, index) => ({
          task_id: c.id,
          order: startOrder + index * orderGap,
          stage_id: destination.toColumnId,
        }))

      submit({
        type: 'set_bulk_task_order',
        updates,
      })

      // clear the sort
      let clearFn = clearSortFnsRef.current[destination.toColumnId]
      if (clearFn) {
        clearFn()
      }
    } else {
      // single card moved in unsorted column
      let order
      let cards = [...destinationColumn.cards]

      if (source.fromColumnId === destination.toColumnId) {
        cards.splice(source.fromPosition, 1)
      }

      let prevCard = cards[destination.toPosition - 1]
      let nextCard = cards[destination.toPosition]

      if (prevCard && nextCard) {
        order = (prevCard.order + nextCard.order) / 2
      } else if (prevCard) {
        order = prevCard.order + 1000
      } else if (nextCard) {
        order = nextCard.order - 1000
      } else {
        order = Date.now()
      }

      // Clear sort only when moving within the same column
      if (source.fromColumnId === destination.toColumnId) {
        let clearFn = clearSortFnsRef.current[destination.toColumnId]
        if (clearFn) {
          clearFn()
        }
      }

      submit({
        type: 'set_task_placement',
        task_id: card.id,
        stage_id: destination.toColumnId,
        order,
      })
    }

    skipSortRef.current = true
    setBoard(currentBoard => moveCard(currentBoard, source, destination))
  }

  let displayBoard = {
    ...board,
    columns: board.columns.map((column, i) => {
      if (column.cards.length === 0) {
        return {
          ...column,
          cards: [
            {
              id: `placeholder-${column.id}`,
              placeholder: true, // this will render the card unable to be dragged
              message:
                i === 0
                  ? `Your tasks will appear here.`
                  : `Move tasks through from start to completion 
with stages.`,
            },
          ],
        }
      }
      return column
    }),
  }

  return (
    <View {...props} onChange={changeFilters}>
      <ControlledBoard
        onCardDragEnd={handleCardMove}
        renderCard={(card, { dragging }) =>
          card.placeholder ? (
            <EmptyCard
              viewPath={`${props.viewPath}/Card(${card.id})`}
              text={card.message}
            />
          ) : (
            <Card
              viewPath={`${props.viewPath}/Card(${card.id})`}
              card={card}
              dragging={dragging}
            />
          )
        }
        renderColumnHeader={column => (
          <StageHeader
            viewPath={`${props.viewPath}/StageHeader(${column.id})`}
            title={column.title}
            count={column.cards.filter(card => !card.placeholder).length}
            stageId={column.id}
            isCurrentUserSubscribed={column.is_current_user_subscribed}
            onSortChange={handleSortChange}
            onRegisterClearSort={handleRegisterClearSort}
          />
        )}
        allowAddCard={false}
        disableColumnDrag
      >
        {displayBoard}
      </ControlledBoard>
    </View>
  )
}
